import { useEffect, useRef, useState } from "react";

type Pattern = "box" | "4-7-8" | "coherent";

const PATTERNS: Record<Pattern, number[]> = {
  box: [4, 4, 4, 4],
  "4-7-8": [4, 7, 8, 0],
  coherent: [5, 0, 5, 0],
};
const PHASE_LABEL = ["Inhale", "Hold", "Exhale", "Hold"];

export function BreathingOrb({
  pattern = "box",
  cycles = 6,
  hue = 44,
  onDone,
}: {
  pattern?: Pattern;
  cycles?: number;
  hue?: number;
  onDone?: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [cycle, setCycle] = useState(1);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const beats = PATTERNS[pattern];

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  function stop() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setRunning(false);
  }

  function runPhase(c: number, p: number) {
    if (p >= beats.length) {
      if (c >= cycles) {
        setRunning(false);
        onDone?.();
        return;
      }
      setCycle(c + 1);
      runPhase(c + 1, 0);
      return;
    }
    const secs = beats[p];
    setPhase(p);
    setRemaining(secs);
    if (secs === 0) return runPhase(c, p + 1);
    let left = secs;
    const tick = () => {
      left -= 1;
      setRemaining(left);
      if (left <= 0) runPhase(c, p + 1);
      else timers.current.push(setTimeout(tick, 1000));
    };
    timers.current.push(setTimeout(tick, 1000));
  }

  function start() {
    setRunning(true);
    setCycle(1);
    runPhase(1, 0);
  }

  const scale = phase === 0 ? 1.25 : phase === 2 ? 0.75 : 1;

  return (
    <div className="flex flex-col items-center gap-4 py-4">
      <div className="relative flex h-56 w-56 items-center justify-center">
        <div
          className="absolute inset-0 rounded-full transition-transform duration-[1200ms] ease-in-out"
          style={{
            transform: `scale(${running ? scale : 1})`,
            background: `radial-gradient(circle at 50% 45%, hsl(${hue} 85% 65% / 0.85), hsl(${hue} 85% 40% / 0.35) 55%, transparent 75%)`,
            boxShadow: `0 0 60px hsl(${hue} 85% 55% / 0.45), inset 0 0 40px hsl(${hue} 85% 70% / 0.45)`,
          }}
        />
        <div className="relative text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-foreground/70">
            {running ? PHASE_LABEL[phase] : "Ready"}
          </div>
          <div className="mt-1 font-serif text-4xl text-gold-luminous">
            {running ? remaining : cycles}
          </div>
          <div className="mt-1 text-[10px] text-foreground/50">
            {running ? `Cycle ${cycle}/${cycles}` : "cycles"}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        {!running ? (
          <button onClick={start} className="rounded-full bg-gold-gradient px-5 py-2 text-sm font-semibold text-obsidian">
            Begin
          </button>
        ) : (
          <button onClick={stop} className="rounded-full border hairline px-5 py-2 text-sm">
            Pause
          </button>
        )}
      </div>
    </div>
  );
}
